import React, { useState } from "react";
import { db } from "../../services/firebase";
import { collection, addDoc } from "firebase/firestore";
import { sendRegisterInterestNotification } from "../../services/emailService";

// ---------------------------------------------------------------------------
// RegisterInterestForm
// ---------------------------------------------------------------------------
// Shown on 'coming-soon' locations instead of booking / enquiry CTAs.
// Writes to the existing public-writable `enquiries` collection (tagged with
// type: "register-interest" + location fields) so no Firestore rules change is
// needed, then fires a location-aware EmailJS notification.
//
// Props:
//   location - the full location config object (needs id, name).
// ---------------------------------------------------------------------------
const RegisterInterestForm = ({ location }) => {
	const locationId = location?.id || "";
	const locationName = location?.name || "Urban 59";

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		phone: "",
		message: "",
	});
	const [submitted, setSubmitted] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState("");

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setSubmitting(true);
		setError("");
		try {
			// Reuse the public-writable `enquiries` collection, tagged so the
			// dashboard can tell register-interest leads apart from enquiries.
			await addDoc(collection(db, "enquiries"), {
				...formData,
				type: "register-interest",
				locationId,
				locationName,
				status: "new",
				createdAt: new Date().toISOString(),
			});

			// Non-blocking, location-aware email notification.
			sendRegisterInterestNotification({
				...formData,
				locationId,
				locationName,
			});

			setSubmitted(true);
		} catch (err) {
			console.error("Register interest submission error:", err);
			setError("Something went wrong. Please try again.");
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<section id="register-interest" className="contact-section">
			<div className="container">
				<div className="row justify-content-center">
					<div className="col-lg-8">
						<div className="contact-form-wrapper">
							{submitted ? (
								<div style={{ textAlign: "center", padding: "3rem 1rem" }}>
									<h2 className="section-title" style={{ color: "#1a1a1a" }}>
										You're <span className="highlight">On The List</span>
									</h2>
									<p
										style={{ fontSize: "1.1rem", color: "rgba(26,26,26,0.6)" }}
									>
										Thanks {formData.firstName}! We'll be in touch as soon as{" "}
										{locationName} opens.
									</p>
								</div>
							) : (
								<>
									<p className="section-label">Coming Soon</p>
									<h2 className="section-title">
										Register Your <span className="highlight">Interest</span>
									</h2>
									<p className="contact-subtitle">
										Be the first to hear when {locationName} opens. Leave your
										details and we'll keep you posted.
									</p>

									{error && (
										<div
											role="alert"
											style={{
												color: "#b42318",
												background: "#fef3f2",
												border: "1px solid #fecdca",
												borderRadius: "8px",
												padding: "0.75rem 1rem",
												marginBottom: "1rem",
											}}
										>
											{error}
										</div>
									)}

									<form onSubmit={handleSubmit} className="contact-form">
										<div className="form-section">
											<div className="row">
												<div className="col-md-6">
													<div className="form-group">
														<label htmlFor="ri-firstName">
															First Name{" "}
															<span className="required">(required)</span>
														</label>
														<input
															type="text"
															id="ri-firstName"
															name="firstName"
															value={formData.firstName}
															onChange={handleChange}
															required
															className="form-control"
														/>
													</div>
												</div>
												<div className="col-md-6">
													<div className="form-group">
														<label htmlFor="ri-lastName">
															Last Name{" "}
															<span className="required">(required)</span>
														</label>
														<input
															type="text"
															id="ri-lastName"
															name="lastName"
															value={formData.lastName}
															onChange={handleChange}
															required
															className="form-control"
														/>
													</div>
												</div>
											</div>
										</div>

										<div className="form-group">
											<label htmlFor="ri-email">
												Email <span className="required">(required)</span>
											</label>
											<input
												type="email"
												id="ri-email"
												name="email"
												value={formData.email}
												onChange={handleChange}
												required
												className="form-control"
											/>
										</div>

										<div className="form-group">
											<label htmlFor="ri-phone">Phone</label>
											<input
												type="tel"
												id="ri-phone"
												name="phone"
												value={formData.phone}
												onChange={handleChange}
												className="form-control"
											/>
										</div>

										<div className="form-group">
											<label htmlFor="ri-message">
												Anything you'd like us to know?
											</label>
											<textarea
												id="ri-message"
												name="message"
												value={formData.message}
												onChange={handleChange}
												rows="4"
												className="form-control"
											/>
										</div>

										<button
											type="submit"
											className="btn-submit"
											disabled={submitting}
										>
											{submitting ? "Sending..." : "Register Interest"}
										</button>
									</form>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default RegisterInterestForm;
